import { useState, useEffect, useCallback, useMemo } from 'react';
import { getProdSupabaseClient, supabase } from '@/lib/supabase/client';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';

export type RecallStatus = 'available' | 'assigned' | 'confirmed';

export interface RecallAssignmentInfo {
  status: RecallStatus;
  technicianId?: string;
  technicianName?: string | undefined;
  expectedVisitDate?: string | undefined;
  assignedAt?: string;
  assignmentId?: string;
  routeId?: string; // Reference to technician_routes.id when recall is part of a route
  stopType?: 'recall' | 'manual'; // Type of stop (recall or manual)
  assignmentStatus?: string; // Raw assignment status for filtering
}

/**
 * Hook to fetch and track the assignment status of recalls
 * Provides a comprehensive view of recall lifecycle: available → assigned → confirmed
 */
export const useRecallAssignmentStatus = () => {
  const { user } = useAuth();
  const [statusMap, setStatusMap] = useState<Map<string, RecallAssignmentInfo>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create a unique key for each recall (VIN + Campaign)
  const createKey = useCallback((vin: string, campaignNumber: string): string => {
    return `${vin}|${campaignNumber}`;
  }, []);

  // Load all assignment statuses
  const loadStatuses = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const newStatusMap = new Map<string, RecallAssignmentInfo>();

      // Try prod schema first, fall back to public schema if it fails
      // technician_assignments is workflow data that may not be accessible in prod schema
      let assignments: any[] | null = null;
      const assignmentsError: any = null;

      const prodClient = getProdSupabaseClient();
      const prodResult = await prodClient
        .from('technician_assignments')
        .select(
          `
          id,
          vin,
          campaign_number,
          technician_id,
          assignment_status,
          expected_visit_date,
          assigned_at,
          route_id,
          stop_type,
          technicians!inner(
            full_name
          )
        `
        )
        .order('assigned_at', { ascending: false });

      // Check if prod schema query failed (e.g., 403 Forbidden)
      if (prodResult.error) {
        // If prod schema fails, fall back to public schema
        console.warn(
          'Failed to fetch assignments from prod schema, falling back to public schema:',
          prodResult.error
        );

        const publicResult = await supabase
          .from('technician_assignments')
          .select(
            `
            id,
            vin,
            campaign_number,
            technician_id,
            assignment_status,
            expected_visit_date,
            assigned_at,
            route_id,
            stop_type,
            technicians!inner(
              full_name
            )
          `
          )
          .order('assigned_at', { ascending: false });

        if (publicResult.error) {
          throw new Error(
            `Failed to fetch assignments from both schemas. Prod error: ${prodResult.error.message}, Public error: ${publicResult.error.message}`
          );
        }

        assignments = publicResult.data;
      } else {
        // Prod schema succeeded
        assignments = prodResult.data;
      }

      // Process assignments and update status map
      assignments?.forEach((assignment: any) => {
        const key = createKey(assignment.vin, assignment.campaign_number);

        // Determine the recall status based on assignment status
        let recallStatus: RecallStatus = 'assigned';

        switch (assignment.assignment_status) {
          case 'assigned':
            recallStatus = 'assigned';
            break;
          case 'confirmed':
            recallStatus = 'confirmed';
            break;
          default:
            recallStatus = 'assigned';
        }

        // Update or add to status map
        // Assignment status takes precedence over selected status
        newStatusMap.set(key, {
          status: recallStatus,
          technicianId: assignment.technician_id,
          technicianName: assignment.technicians?.full_name || undefined,
          expectedVisitDate: assignment.expected_visit_date || undefined,
          assignedAt: assignment.assigned_at,
          assignmentId: assignment.id,
          routeId: assignment.route_id || undefined,
          stopType: assignment.stop_type || 'recall',
          assignmentStatus: assignment.assignment_status
        });
      });

      setStatusMap(newStatusMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load recall statuses');
    } finally {
      setLoading(false);
    }
  }, [user?.id, createKey]);

  // Load statuses on mount and when user changes
  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  // Get status for a specific recall
  const getRecallStatus = useCallback(
    (vin: string, campaignNumber: string): RecallAssignmentInfo => {
      const key = createKey(vin, campaignNumber);
      return statusMap.get(key) || { status: 'available' };
    },
    [statusMap, createKey]
  );

  // Check if a recall has a specific status
  const hasStatus = useCallback(
    (vin: string, campaignNumber: string, status: RecallStatus): boolean => {
      const info = getRecallStatus(vin, campaignNumber);
      return info.status === status;
    },
    [getRecallStatus]
  );

  // Get all recalls with a specific status
  const getRecallsByStatus = useCallback(
    (status: RecallStatus): string[] => {
      const results: string[] = [];
      statusMap.forEach((info, key) => {
        if (info.status === status) {
          results.push(key);
        }
      });
      return results;
    },
    [statusMap]
  );

  // Check if a recall is part of a route
  const isInRoute = useCallback(
    (vin: string, campaignNumber: string): boolean => {
      const info = getRecallStatus(vin, campaignNumber);
      return !!info.routeId;
    },
    [getRecallStatus]
  );

  // Get all recalls that are in routes (have a route_id)
  const getRecallsInRoutes = useCallback((): Set<string> => {
    const inRoutes = new Set<string>();
    statusMap.forEach((info, key) => {
      if (info.routeId) {
        inRoutes.add(key);
      }
    });
    return inRoutes;
  }, [statusMap]);

  // Check if a recall is a completed manual stop
  const isCompletedManualStop = useCallback(
    (vin: string, campaignNumber: string): boolean => {
      const info = getRecallStatus(vin, campaignNumber);
      return info.stopType === 'manual' && info.assignmentStatus === 'completed';
    },
    [getRecallStatus]
  );

  // Get all completed manual stops (for filtering in views)
  const completedManualStops = useMemo((): Set<string> => {
    const completed = new Set<string>();
    statusMap.forEach((info, key) => {
      if (info.stopType === 'manual' && info.assignmentStatus === 'completed') {
        completed.add(key);
      }
    });
    return completed;
  }, [statusMap]);

  // Recalls that are "done/removed" via the route tracker and should be hidden
  // from work views: completed, or cancelled (vehicle sold / other). Recalls that
  // auto-unassign (not_available / incomplete) revert to available and are NOT
  // included here, so they remain visible as outstanding work.
  const removedRecallKeys = useMemo((): Set<string> => {
    const removed = new Set<string>();
    statusMap.forEach((info, key) => {
      if (info.assignmentStatus === 'completed' || info.assignmentStatus === 'cancelled') {
        removed.add(key);
      }
    });
    return removed;
  }, [statusMap]);

  // Computed statistics
  const stats = useMemo(() => {
    const statsByStatus: Record<RecallStatus, number> = {
      available: 0,
      assigned: 0,
      confirmed: 0,
      in_progress: 0,
      completed: 0,
      cancelled: 0
    };

    statusMap.forEach(info => {
      statsByStatus[info.status]++;
    });

    return {
      total: statusMap.size,
      byStatus: statsByStatus
    };
  }, [statusMap]);

  return {
    loading,
    error,
    statusMap,
    getRecallStatus,
    hasStatus,
    getRecallsByStatus,
    isInRoute,
    getRecallsInRoutes,
    isCompletedManualStop,
    completedManualStops,
    removedRecallKeys,
    stats,
    refreshStatuses: loadStatuses
  };
};
